import React, { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Alert, AlertDescription } from "./ui/alert";
import { Info, AlertCircle } from 'lucide-react';

export default function DatabaseDataSourceForm({ agents = [], initialData, onSubmit, onCancel, isLoading }) {
    const isEditing = !!initialData;
    
    // Handle both snake_case and PascalCase field names from backend
    const getInitialValue = (snakeCase, pascalCase) => {
        if (!initialData) return '';
        return initialData[snakeCase] || initialData[pascalCase] || '';
    };
    
    const initialDbType = getInitialValue('db_type', 'DBType') || 'POSTGRESQL';
    const initialDbSchema = getInitialValue('db_schema', 'DBSchema')
        || (initialDbType === 'POSTGRESQL' ? 'public' : '');

    const [dbInfo, setDbInfo] = useState({
        dbType: initialDbType,
        dbHost: getInitialValue('db_host', 'DBHost'),
        dbPort: getInitialValue('db_port', 'DBPort'),
        dbName: getInitialValue('db_name', 'DBName'),
        dbSchema: initialDbSchema,
        dbUser: getInitialValue('db_user', 'DBUser'),
        agentId: getInitialValue('agent_id', 'AgentId'),
        isEnabled: initialData?.is_enabled !== undefined ? initialData.is_enabled : 
                   initialData?.IsEnabled !== undefined ? initialData.IsEnabled : true,
    });
    const [error, setError] = useState(null);

    // Update state if initialData changes (e.g., when editing)
    useEffect(() => {
        if (initialData) {
            const updatedDbType = getInitialValue('db_type', 'DBType') || 'POSTGRESQL';
            const updatedSchema = getInitialValue('db_schema', 'DBSchema')
                || (updatedDbType === 'POSTGRESQL' ? 'public' : '');

            setDbInfo({
                dbType: updatedDbType,
                dbHost: getInitialValue('db_host', 'DBHost'),
                dbPort: getInitialValue('db_port', 'DBPort'),
                dbName: getInitialValue('db_name', 'DBName'),
                dbSchema: updatedSchema,
                dbUser: getInitialValue('db_user', 'DBUser'),
                agentId: getInitialValue('agent_id', 'AgentId'),
                isEnabled: initialData?.is_enabled !== undefined ? initialData.is_enabled : 
                           initialData?.IsEnabled !== undefined ? initialData.IsEnabled : true,
            });
        }
    }, [initialData]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setDbInfo(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSelectChange = (name, value) => {
        setDbInfo(prev => {
            const next = {
                ...prev,
                [name]: value
            };

            if (name === 'dbType') {
                next.dbSchema = value === 'POSTGRESQL'
                    ? (prev.dbSchema || 'public')
                    : '';
            }

            return next;
        });
    };

    // Validate connection string format
    const validateConnectionInfo = () => {
        // Check required fields
        if (!dbInfo.dbType || !dbInfo.dbHost || !dbInfo.dbPort || !dbInfo.dbName || !dbInfo.dbUser || !dbInfo.agentId) {
            return "Please fill in all required database details and select an agent.";
        }

        // Validate host format (basic check)
        const hostRegex = /^[a-zA-Z0-9.-]+$/;
        if (!hostRegex.test(dbInfo.dbHost)) {
            return "Invalid host format. Use only letters, numbers, dots, and hyphens.";
        }

        // Validate port
        const portNumber = parseInt(dbInfo.dbPort, 10);
        if (isNaN(portNumber) || portNumber <= 0 || portNumber > 65535) {
            return "Please enter a valid port number (1-65535).";
        }

        // Validate database name (alphanumeric, underscore, and dash)
        const dbNameRegex = /^[a-zA-Z0-9_\-]+$/;
        if (!dbNameRegex.test(dbInfo.dbName)) {
            return "Database name should contain only letters, numbers, underscores, and dashes.";
        }

        // Validate username (alphanumeric, underscore, and dash)
        const userRegex = /^[a-zA-Z0-9_\-]+$/;
        if (!userRegex.test(dbInfo.dbUser)) {
            return "Username should contain only letters, numbers, underscores, and dashes.";
        }

        return null;
    };

    const handleSubmitInternal = (e) => {
        e.preventDefault();
        setError(null);

        // Only validate if not editing (when editing, fields are read-only)
        if (!isEditing) {
            const validationError = validateConnectionInfo();
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        // Convert port to number before submitting
        const portNumber = parseInt(dbInfo.dbPort, 10);

        const schemaValue = dbInfo.dbSchema ? dbInfo.dbSchema.trim() : '';

        const dataToSubmit = {
            db_type: dbInfo.dbType,
            db_host: dbInfo.dbHost,
            db_port: portNumber,
            db_name: dbInfo.dbName,
            ...(schemaValue ? { db_schema: schemaValue } : {}),
            db_user: dbInfo.dbUser,
            agent_id: dbInfo.agentId,
            is_enabled: dbInfo.isEnabled,
        };

        onSubmit(dataToSubmit);
    };

    // Generate example connection string
    const getExampleConnectionString = () => {
        const defaultPort = dbInfo.dbType === 'POSTGRESQL' ? 5432 : 3306;
        const scheme = dbInfo.dbType === 'POSTGRESQL' ? 'postgresql' : 'mysql';
        const host = dbInfo.dbHost || 'localhost';
        const port = dbInfo.dbPort || defaultPort;
        const database = dbInfo.dbName || 'database';
        const user = dbInfo.dbUser || 'username';
        
        return `${scheme}://${user}:password@${host}:${port}/${database}`;
    };

    return (
        <div className="space-y-4">
            <form onSubmit={handleSubmitInternal} className="space-y-4">
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {isEditing ? (
                    <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                            <strong>Note:</strong> Connection details cannot be modified after creation. 
                            To change connection parameters, please delete this data source and create a new one.
                        </AlertDescription>
                    </Alert>
                ) : (
                    <Alert>
                        <Info className="h-4 w-4" />
                        <AlertDescription>
                            Enter your database connection details. The agent will use these details to connect securely to your database.
                        </AlertDescription>
                    </Alert>
                )}

                {/* DB Type */}
                <div>
                    <Label htmlFor="dbType">Database Type</Label>
                    <Select
                        value={dbInfo.dbType}
                        onValueChange={(value) => handleSelectChange('dbType', value)}
                        name="dbType"
                        disabled={isEditing}
                    >
                        <SelectTrigger id="dbType" disabled={isEditing}>
                            <SelectValue placeholder="Select DB Type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="POSTGRESQL">PostgreSQL</SelectItem>
                            <SelectItem value="MYSQL">MySQL</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Host and Port */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="dbHost">Host</Label>
                        <Input
                            id="dbHost"
                            name="dbHost"
                            value={dbInfo.dbHost}
                            onChange={handleInputChange}
                            placeholder="e.g., localhost or db.example.com"
                            required
                            disabled={isEditing}
                        />
                    </div>
                    <div>
                        <Label htmlFor="dbPort">Port</Label>
                        <Input
                            id="dbPort"
                            name="dbPort"
                            type="number"
                            value={dbInfo.dbPort}
                            onChange={handleInputChange}
                            placeholder={dbInfo.dbType === 'POSTGRESQL' ? '5432' : '3306'}
                            required
                            disabled={isEditing}
                        />
                    </div>
                </div>

                {/* Schema */}
                <div>
                    <Label htmlFor="dbSchema">Schema (optional)</Label>
                    <Input
                        id="dbSchema"
                        name="dbSchema"
                        value={dbInfo.dbSchema || ''}
                        onChange={handleInputChange}
                        placeholder={dbInfo.dbType === 'POSTGRESQL' ? 'public' : dbInfo.dbName || 'database'}
                        disabled={isEditing}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Use when your tables live outside the default schema.
                    </p>
                </div>

                {/* DB Name and User */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="dbName">Database Name</Label>
                        <Input
                            id="dbName"
                            name="dbName"
                            value={dbInfo.dbName}
                            onChange={handleInputChange}
                            placeholder="e.g., myapp_db"
                            required
                            disabled={isEditing}
                        />
                    </div>
                    <div>
                        <Label htmlFor="dbUser">Read-Only User</Label>
                        <Input
                            id="dbUser"
                            name="dbUser"
                            value={dbInfo.dbUser}
                            onChange={handleInputChange}
                            placeholder="e.g., readonly_user"
                            required
                            disabled={isEditing}
                        />
                        <p className="text-xs text-gray-500 mt-1">Ensure this user has only read permissions.</p>
                    </div>
                </div>

                {/* Connection String Example */}
                <div className="space-y-2">
                    <Label className="text-sm">Connection String Format:</Label>
                    <div className="bg-gray-50 p-3 rounded-md">
                        <code className="text-xs text-gray-700 break-all">
                            {getExampleConnectionString()}
                        </code>
                    </div>
                    <p className="text-xs text-gray-500">
                        The agent will need the password when connecting. Never share passwords in the UI.
                    </p>
                </div>

                {/* Agent Selection */}
                <div>
                    <Label htmlFor="agentId">Agent</Label>
                    <Select
                        value={dbInfo.agentId}
                        onValueChange={(value) => handleSelectChange('agentId', value)}
                        name="agentId"
                        required
                        disabled={agents.length === 0 || isEditing}
                    >
                        <SelectTrigger id="agentId" disabled={agents.length === 0 || isEditing}>
                            <SelectValue placeholder={agents.length === 0 ? "No agents available" : "Select an Agent"} />
                        </SelectTrigger>
                        <SelectContent>
                            {agents.map(agent => (
                                <SelectItem key={agent.agent_id} value={agent.agent_id}>
                                    Agent ID: {agent.agent_id} ({agent.status || 'UNKNOWN'})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {agents.length === 0 && !isEditing && <p className="text-xs text-red-500 mt-1">No agents found. Please create an agent first.</p>}
                </div>

                {/* Enabled Checkbox */}
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="isEnabled"
                        name="isEnabled"
                        checked={dbInfo.isEnabled}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <Label htmlFor="isEnabled" className="text-sm font-medium text-gray-700">
                        Enabled
                    </Label>
                    {isEditing && (
                        <span className="text-xs text-gray-500 ml-2">(This is the only field that can be modified)</span>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                        {isLoading ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Database Source' : 'Add Database Source')}
                    </Button>
                </div>
            </form>
        </div>
    );
}
